import React, { Fragment } from 'react';
import get from 'lodash.get';
import {
    ChevronDownIcon,
    ChevronUpDownIcon,
    ChevronUpIcon,
    ChevronRightIcon,
} from '@heroicons/react/24/outline';
import { APP_BG } from '@/constant/theme/colors.constant';
import { Text } from '@/components/atoms/AppText';
import { tid } from '@/utils/testId';
import { ORGANISM_TEST_IDS } from '@/constant/testIds/oganism.testIds';
import type { ColumnDef, SortState } from './types';
import SkeletonCards from './SkeletonCards';

// ─── Props ────────────────────────────────────────────────────────────────────

export interface MobileCardViewProps<T> {
    data: T[];
    columns: ColumnDef<T>[];
    loading?: boolean;
    skeletonRows?: number;
    sortState?: SortState;
    onSort?: (accessor: keyof T | string) => void;
    renderExpandedRow?: (item: T) => React.ReactNode;
    expandedRowDetail?: T | null;
    activeExpandedIndex?: number | null;
    onExpandToggle?: (index: number, item: T) => void;
    showChevronCol?: boolean;
    rowKey?: (item: T, index: number) => string | number;
    rowTestId?: (item: T, index: number) => string;
    className?: string;
}

// ─── Helpers ─────────────────────────────────────────────────────────────────

/** Tiny muted uppercase label used on every field dt */
const FieldLabel: React.FC<{ children: React.ReactNode }> = ({ children }) => (
    <Text
        as="span"
        size="xs"
        weight="semibold"
        color="subtle"
        uppercase
        tracking="wider"
        className="block leading-none mb-1"
    >
        {children}
    </Text>
);

// ─── Component ───────────────────────────────────────────────────────────────

function MobileCardView<T>({
    data = [],
    columns,
    loading = false,
    skeletonRows = 6,
    sortState,
    onSort,
    renderExpandedRow,
    expandedRowDetail,
    activeExpandedIndex,
    onExpandToggle,
    showChevronCol,
    rowKey,
    rowTestId,
    className = '',
}: MobileCardViewProps<T>) {
    const { DATA_TABLE_CARD_TID } = ORGANISM_TEST_IDS;
    const hasExpand = !!renderExpandedRow && !!showChevronCol;

    // Column roles
    const actionCol = columns.find((c) => c.accessor === 'action');
    const dataCols = columns.filter((c) => c.accessor !== 'action');
    const titleCol = dataCols[0];
    const bodyCols = dataCols.slice(1);
    const sortableCols = columns.filter((c) => c.sorting);

    // ── Loading ─────────────────────────────────────────────────────────────
    if (loading) {
        return (
            <div className={[className, APP_BG.canvas].join(' ')}>
                <SkeletonCards
                    rows={skeletonRows}
                    cols={dataCols.length}
                    hasExpand={hasExpand}
                    hasSortBar={sortableCols.length > 0 && !!onSort}
                />
            </div>
        );
    }

    if (data.length === 0) return null;

    return (
        <div
            className={[className, APP_BG.canvas].join(' ')}
            data-testid={tid(DATA_TABLE_CARD_TID.CARD_LIST)}
        >
            {/* ── Sort bar ─────────────────────────────────────────────────────
                Sticky, full-width strip. Chips scroll horizontally on overflow.
            ─────────────────────────────────────────────────────────────────── */}
            {sortableCols.length > 0 && onSort && (
                <div
                    data-testid={tid(DATA_TABLE_CARD_TID.SORT_BAR)}
                    className={[
                        'sticky top-0 z-20',
                        'flex items-center gap-0',
                        'border-b border-gray-100 dark:border-white/[0.06]',
                        APP_BG.elevated,
                    ].join(' ')}
                >
                    {/* "Sort by" label */}
                    <span className="shrink-0 px-3 text-[10px] font-bold uppercase tracking-widest text-gray-400 dark:text-gray-600 border-r border-gray-100 dark:border-white/[0.06] self-stretch flex items-center">
                        Sort
                    </span>

                    {/* Scrollable chip row */}
                    <div className="flex items-center gap-0 overflow-x-auto scrollbar-none divide-x divide-gray-100 dark:divide-white/[0.06]">
                        {sortableCols.map((col) => {
                            const isActive = sortState?.orderBy === col.accessor;
                            const order = isActive ? sortState?.order : undefined;

                            return (
                                <button
                                    key={String(col.accessor)}
                                    type="button"
                                    data-testid={tid(`${DATA_TABLE_CARD_TID.SORT_CHIP}-${String(col.accessor)}`)}
                                    onClick={() => onSort(col.accessor)}
                                    className={[
                                        'inline-flex items-center gap-1 shrink-0 whitespace-nowrap',
                                        'px-3 py-2.5 h-full',
                                        'text-[11px] font-semibold',
                                        'transition-colors duration-150 focus:outline-none',
                                        isActive
                                            ? 'bg-primary/[0.07] dark:bg-primary/[0.10] text-primary'
                                            : 'bg-transparent text-gray-500 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-white/[0.04] hover:text-gray-700 dark:hover:text-gray-200',
                                    ].join(' ')}
                                >
                                    {col.header}
                                    {order === 'asc' && <ChevronUpIcon className="h-3 w-3 text-primary" strokeWidth={2.5} />}
                                    {order === 'desc' && <ChevronDownIcon className="h-3 w-3 text-primary" strokeWidth={2.5} />}
                                    {!order && <ChevronUpDownIcon className="h-3 w-3 opacity-30" strokeWidth={2} />}
                                </button>
                            );
                        })}
                    </div>
                </div>
            )}

            {/* ── Card list ────────────────────────────────────────────────────
                Cards are inset from the DataTable edge with mx-3.
                Vertical gap between cards: gap-2.5.
            ─────────────────────────────────────────────────────────────────── */}
            <div className="flex flex-col gap-2.5 py-3">
                {data.map((item, index) => {
                    const key = rowKey ? rowKey(item, index) : index;
                    const isExpanded = activeExpandedIndex === index;
                    const testId = rowTestId
                        ? rowTestId(item, index)
                        : tid(`${DATA_TABLE_CARD_TID.CARD}-${index}`);

                    const titleRaw = titleCol ? get(item, titleCol.accessor as string) : null;
                    const titleContent = titleCol?.render ? titleCol.render(item, index) : titleRaw;

                    return (
                        <Fragment key={key}>
                            {/* ══ Card ════════════════════════════════════════════ */}
                            <div
                                data-testid={testId}
                                role={showChevronCol ? 'button' : undefined}
                                aria-expanded={showChevronCol ? isExpanded : undefined}
                                onClick={showChevronCol ? () => onExpandToggle?.(index, item) : undefined}
                                className={[
                                    'rounded-2xl overflow-hidden transition-all duration-200',
                                    showChevronCol ? 'cursor-pointer' : '',
                                    // border + shadow
                                    isExpanded
                                        ? 'border border-primary/25 dark:border-primary/20 shadow-[0_2px_12px_0_rgba(0,0,0,0.08)] dark:shadow-[0_2px_16px_0_rgba(0,0,0,0.32)]'
                                        : 'border border-gray-200/90 dark:border-white/[0.07] shadow-[0_1px_4px_0_rgba(0,0,0,0.05)] dark:shadow-[0_1px_8px_0_rgba(0,0,0,0.22)] hover:shadow-[0_2px_10px_0_rgba(0,0,0,0.09)] dark:hover:shadow-[0_2px_14px_0_rgba(0,0,0,0.30)] hover:border-gray-300/80 dark:hover:border-white/[0.11]',
                                    APP_BG.base,
                                ].join(' ')}
                            >
                                {/* ── Header ──────────────────────────────────────
                                    Primary identifier on the left.
                                    Action button isolated on the right.
                                ─────────────────────────────────────────────── */}
                                <div className="flex items-start justify-between gap-3 px-4 pt-4 pb-3">
                                    {titleCol && (
                                        <div className="flex-1 min-w-0">
                                            <FieldLabel>{titleCol.header}</FieldLabel>
                                            <div className="text-[13px] font-semibold leading-snug text-gray-800 dark:text-gray-100 truncate">
                                                {titleContent ?? (
                                                    <span className="font-normal text-gray-400 dark:text-gray-600">—</span>
                                                )}
                                            </div>
                                        </div>
                                    )}
                                    {actionCol && (
                                        <div
                                            className="shrink-0 -mr-0.5"
                                            onClick={(e) => e.stopPropagation()}
                                        >
                                            {actionCol.render
                                                ? actionCol.render(item, index)
                                                : (get(item, actionCol.accessor as string) as React.ReactNode)}
                                        </div>
                                    )}
                                </div>

                                {/* ── Body ────────────────────────────────────────
                                    2-column field grid. Columns with noTruncate
                                    span the full width.
                                ─────────────────────────────────────────────── */}
                                {bodyCols.length > 0 && (
                                    <>
                                        <div className="mx-4 h-px bg-gray-100 dark:bg-white/[0.05]" />
                                        <dl className="grid grid-cols-2 gap-x-5 gap-y-4 px-4 pt-3 pb-4">
                                            {bodyCols.map((col, ci) => {
                                                const rawValue = get(item, col.accessor as string);
                                                const content = col.render ? col.render(item, index) : rawValue;
                                                const span2 = col.noTruncate;

                                                return (
                                                    <div
                                                        key={ci}
                                                        className={['min-w-0', span2 ? 'col-span-2' : ''].join(' ')}
                                                    >
                                                        <dt>
                                                            <FieldLabel>{col.header}</FieldLabel>
                                                        </dt>
                                                        <dd className="min-w-0">
                                                            <div
                                                                className={[
                                                                    'text-[12px] font-medium leading-snug',
                                                                    'text-gray-700 dark:text-gray-200',
                                                                    span2 ? 'break-words' : 'truncate',
                                                                ].join(' ')}
                                                            >
                                                                {content ?? (
                                                                    <span className="font-normal text-gray-400 dark:text-gray-600">—</span>
                                                                )}
                                                            </div>
                                                        </dd>
                                                    </div>
                                                );
                                            })}
                                        </dl>
                                    </>
                                )}

                                {/* ── Footer (expandable rows only) ───────────────
                                    Row counter left • "Details" button right
                                ─────────────────────────────────────────────── */}
                                {showChevronCol && (
                                    <div
                                        className={[
                                            'flex items-center justify-between',
                                            'px-4 py-2.5',
                                            'border-t border-gray-100 dark:border-white/[0.05]',
                                            isExpanded
                                                ? 'bg-primary/[0.03] dark:bg-primary/[0.05]'
                                                : 'bg-gray-50/60 dark:bg-white/[0.015]',
                                        ].join(' ')}
                                    >
                                        <Text as="span" size="xs" color="subtle" weight="medium">
                                            #{index + 1}
                                        </Text>
                                        <button
                                            type="button"
                                            data-testid={tid(`${DATA_TABLE_CARD_TID.CARD_EXPAND_BTN}-${index}`)}
                                            onClick={(e) => { e.stopPropagation(); onExpandToggle?.(index, item); }}
                                            className={[
                                                'inline-flex items-center gap-1.5',
                                                'rounded-full px-3 py-1.5',
                                                'text-[11px] font-semibold',
                                                'border transition-all duration-150 focus:outline-none',
                                                isExpanded
                                                    ? 'border-primary/30 bg-primary/[0.08] dark:bg-primary/[0.12] text-primary'
                                                    : 'border-transparent bg-white dark:bg-white/[0.06] text-gray-500 dark:text-gray-400 hover:border-gray-200 dark:hover:border-white/[0.10] hover:text-gray-700 dark:hover:text-gray-200 shadow-[0_1px_3px_0_rgba(0,0,0,0.06)]',
                                            ].join(' ')}
                                        >
                                            {isExpanded ? 'Collapse' : 'Details'}
                                            <ChevronRightIcon
                                                className={['h-3 w-3 transition-transform duration-200', isExpanded ? 'rotate-90' : ''].join(' ')}
                                                strokeWidth={2.5}
                                            />
                                        </button>
                                    </div>
                                )}
                            </div>

                            {/* ══ Expanded detail panel ═══════════════════════════
                                Rendered as a sibling card below, slightly inset,
                                with a connecting top accent line.
                            ════════════════════════════════════════════════════ */}
                            {hasExpand && isExpanded && (
                                <div
                                    data-testid={tid(`${DATA_TABLE_CARD_TID.CARD_EXPAND_PANEL}-${index}`)}
                                    className={[
                                        'rounded-2xl border overflow-hidden',
                                        'border-primary/20 dark:border-primary/15',
                                        'bg-gray-50/70 dark:bg-[#0b1018]',
                                        'shadow-[0_1px_6px_0_rgba(0,0,0,0.06)] dark:shadow-[0_1px_10px_0_rgba(0,0,0,0.28)]',
                                        'px-4 py-4',
                                        'animate-[expandDown_220ms_cubic-bezier(0.16,1,0.3,1)]',
                                    ].join(' ')}
                                >
                                    {renderExpandedRow!(expandedRowDetail ?? item)}
                                </div>
                            )}
                        </Fragment>
                    );
                })}
            </div>
            {/* Bottom spacer — keeps the last card above the fixed pagination pill */}
            <div className="h-14" aria-hidden="true" />
        </div>
    );
}

export default MobileCardView;

